import { useCallback, useMemo } from 'react'
import { AclRoute, holdsAny, usePermissions } from '@/lib/acl'
import { useCrmEntitlements } from '@/lib/proposals/use-proposals'
import { type CrmNavItem, visibleCrmNav } from './crm-nav'
import { useCrmPreviewEnabled } from './crm-preview-flag'

export interface CrmAccess {
  /** True until both the tenant entitlement and the caller's CRM keys have resolved. */
  isLoading: boolean
  /** The tenant holds the `crm_core` entitlement. */
  holdsCrmCore: boolean
  /** The tenant holds `crm_core` AND the caller holds `key` (or any of `key`). Fail-closed. */
  can: (key: string | readonly string[]) => boolean
  /** The CRM tabs this caller can open, in sidebar order (`CRM_NAV`). */
  visibleNav: CrmNavItem[]
  /** The caller can open at least one CRM tab: shows the CRM tool in the switcher and sidebar. */
  canOpenCrm: boolean
}

/**
 * Two gates decide every CRM control, and both must pass:
 *  1. the tenant's `crm_core` entitlement (`crmEntitlements`, the one entitlement-exempt read —
 *     crm-svc refuses every other call from a tenant without it);
 *  2. the caller's UI key on the `CRM` route, which maps to the crm-svc permission the control's
 *     operation checks (see `CrmAclKeys`).
 * Both fail closed: while either loads or errors, nothing is offered.
 */
export function useCrmAccess(): CrmAccess {
  const entitlements = useCrmEntitlements()
  const { isLoading, checkPermission } = usePermissions(AclRoute.CRM)
  const holdsCrmCore = entitlements.data?.includes('crm_core') ?? false
  const previewEnabled = useCrmPreviewEnabled()

  const can = useCallback(
    (key: string | readonly string[]) => holdsCrmCore && holdsAny(checkPermission, key),
    [holdsCrmCore, checkPermission],
  )

  const visibleNav = useMemo(() => visibleCrmNav(can, previewEnabled), [can, previewEnabled])

  return {
    isLoading: entitlements.isLoading || isLoading,
    holdsCrmCore,
    can,
    visibleNav,
    canOpenCrm: visibleNav.length > 0,
  }
}
